import React, { useEffect, useState } from 'react';
import { PositionedModal } from 'common/components/PositionedModal';
import { StyledCancelButton, StyledSaveButton } from '@/common/components/button/StyledButton';
import { useCreatePlansCultureActionMutation, useUpdatePlansCultureActionMutation } from '@/areas/culture-hub/api/plansCultureActions';
import { useToasts } from 'common/toast';
import { ModalFieldLabel, PageHeader } from '@/common/styles/page';
import { INVOLVED_OPTIONS, renderInvolvedIcon } from './involvedHelpers';
import { FormField } from 'semantic-ui-react';
import { Input } from '@/common/ui/input';
import { Textarea } from '@/common/ui/textarea';

interface CultureActionModalProps {
  open: boolean;
  onClose: () => void;
  actionPlanId?: number | null; // parent action plan id (assessment period action plan id)
  initial?: any; // existing culture action to edit
}

const defaultModel = {
  action_taken: '',
  involved_party: undefined as number | undefined,
  observations: '',
  next_actions: '',
  impact: undefined as number | undefined,
};

const IMPACT_LEVELS = [
  // Recolored to a positive green gradient (light → dark)
  { value: 0, label: 'None', bg: 'bg-green-100', color: '#DCFCE7' },   // very pale
  { value: 1, label: 'Low', bg: 'bg-green-200', color: '#86EFAC' },    // light
  { value: 2, label: 'Medium', bg: 'bg-green-300', color: '#4ADE80' }, // medium
  { value: 3, label: 'High', bg: 'bg-green-600', color: '#16A34A' },   // strong
  { value: 4, label: 'Huge', bg: 'bg-green-800', color: '#09815f' },   // dark
];

// derived values/helpers so we don't hardcode the max
const maxImpact = IMPACT_LEVELS.length - 1;
const getRangeBackground = (val: number) => {
  const percent = (val / maxImpact) * 100;
  const color = IMPACT_LEVELS[val]?.color ?? IMPACT_LEVELS[1].color;
  // left portion is colored, right portion is the gray track
  return `linear-gradient(90deg, ${color} ${percent}%, #e5e7eb ${percent}%)`;
};

// uses INVOLVED_OPTIONS from shared helper (involvedHelpers)

// icon rendering moved to shared `involvedHelpers` (renderInvolvedIcon)


export const CultureActionModal: React.FC<CultureActionModalProps> = ({ open, onClose, actionPlanId, initial }) => {
  const [form, setForm] = useState<any>({ ...defaultModel });
  const [create] = useCreatePlansCultureActionMutation();
  const [update] = useUpdatePlansCultureActionMutation();
  const { addToast } = useToasts();

  useEffect(() => {
    if (initial) {
      setForm({
        action_taken: initial.action_taken ?? '',
        involved_party: initial.involved_party ?? undefined,
        observations: initial.observations ?? '',
        next_actions: initial.next_actions ?? '',
        impact: initial.impact ?? undefined,
      });
    } else {
      setForm({ ...defaultModel });
    }
  }, [initial, open]);

  const handleChange = (key: string, value: any) => setForm((s: any) => ({ ...s, [key]: value }));

  const handleSave = async () => {
    try {
      if (!actionPlanId && !initial) {
        addToast('No action plan selected. Cannot add culture action.', { appearance: 'error', autoDismiss: true });
        return;
      }

      if (initial && initial.id) {
        // update
        const payload = {
          ...form,
          // prefer explicit existing association, otherwise use passed actionPlanId
          assessment_period_action_plan: initial.assessment_period_action_plan ?? actionPlanId ?? undefined,
        };

        try {
          await update({
            id: initial.id,
            assessmentPeriodActionPlanCultureActionId: initial.id,
            assessmentPeriodActionPlanCultureAction: payload,
          }).unwrap();
          addToast('Culture action updated', { appearance: 'success', autoDismiss: true });
        } catch (err: any) {
          console.error('Failed to update culture action (payload):', payload, err);
          throw err;
        }
      } else {
        // create
        if (!actionPlanId) {
          addToast('No action plan selected. Cannot add culture action.', { appearance: 'error', autoDismiss: true });
          return;
        }

        const payload = {
          ...form,
          assessment_period_action_plan: actionPlanId,
        };

        try {
          await create({
            assessmentPeriodActionPlanCultureAction: payload,
          }).unwrap();
          addToast('Culture action added', { appearance: 'success', autoDismiss: true });
        } catch (err: any) {
          console.error('Failed to save culture action (payload):', payload, err);
          throw err;
        }
      }

      onClose();
    } catch (err: any) {
      console.error('Failed to save culture action:', err);
      addToast('Failed to save culture action', { appearance: 'error', autoDismiss: true });
    }
  };

  return (
    <PositionedModal open={open} onClose={onClose} size="small">
      <div className="pt-6 pl-6 pr-8 mb-6">
        <PageHeader title={initial ? 'Edit Culture Action' : 'Add Culture Action'} divider />
        <div className="mb-6">
          <ModalFieldLabel>Action Taken</ModalFieldLabel>
          <Textarea rows={3} value={form.action_taken} onChange={(e: any) => handleChange('action_taken', e.target.value)} />
        </div>

        <div className="mb-6">
          <ModalFieldLabel>People Involved</ModalFieldLabel>

          <div role="radiogroup" aria-label="Involved party" className="grid grid-cols-3 gap-2">

            {INVOLVED_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={form.involved_party === opt.value}
                tabIndex={0}
                onClick={() => handleChange('involved_party', opt.value)}
                onKeyDown={(e: any) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleChange('involved_party', opt.value); } }}
                className={`flex items-center gap-3 px-3 py-2 rounded border text-left ${form.involved_party === opt.value ? 'border-gray-300 bg-blue-50 ' : 'border-gray-200 bg-white'}`}>
                <div className="w-5 h-5 flex items-center justify-center" aria-hidden title={opt.label}>
                  {renderInvolvedIcon(opt.value, 16)}
                </div>
                <div className="text-sm">
                  <div className={`${form.involved_party === opt.value ? 'font-semibold' : 'font-normal text-gray-600'}`}>{opt.label}</div>
                </div>
              </button>
            ))}
          </div>

        </div>


        <div className="mb-6">
          <ModalFieldLabel>Observations</ModalFieldLabel>
          <Textarea rows={3} value={form.observations} onChange={(e: any) => handleChange('observations', e.target.value)} />
        </div>



        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="col-span-2">
            <div className="flex items-center gap-3">
              <ModalFieldLabel>Impact</ModalFieldLabel>
              <div className="ml-auto w-full flex justify-end">
                <div className="w-full">
                  <input
                    type="range"
                    min={0}
                    max={maxImpact}
                    step={1}
                    value={form.impact ?? 0}
                    onChange={(e: any) => handleChange('impact', Number(e.target.value))}
                    className="w-full h-2 mt-0 appearance-none rounded impact-range"
                    style={{ background: getRangeBackground(form.impact ?? 0), ['--impact-color' as any]: IMPACT_LEVELS[(form.impact ?? 0)].color }}
                    aria-label="Impact"
                    aria-valuemin={0}
                    aria-valuemax={maxImpact}
                    aria-valuenow={form.impact ?? 0}
                  />

                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    {IMPACT_LEVELS.map((l) => (
                      <button key={l.value} type="button" onClick={() => handleChange('impact', l.value)} className="text-xs">{l.label}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <style>{`
                  .impact-range::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 14px;
                    height: 14px;
                    border-radius: 9999px;
                    background: var(--impact-color);
                    border: 2px solid #ffffff;
                    box-shadow: 0 0 0 4px rgba(0,0,0,0.05);
                    cursor: pointer;
                    margin-top: -3px; /* better vertical alignment */
                  }
                  .impact-range::-moz-range-thumb {
                    width: 14px;
                    height: 14px;
                    border-radius: 50%;
                    background: var(--impact-color);
                    border: 2px solid #ffffff;
                    box-shadow: 0 0 0 4px rgba(0,0,0,0.05);
                    cursor: pointer;
                    transform: translateY(-3px); /* firefox offset */
                  }
                  .impact-range::-webkit-slider-runnable-track {
                    height: 8px;
                    border-radius: 9999px;
                  }
                  .impact-range::-moz-range-track {
                    height: 8px;
                    border-radius: 9999px;
                  }
                  .impact-range:focus::-webkit-slider-thumb {
                    box-shadow: 0 0 0 6px rgba(0,0,0,0.08);
                  }
                  .impact-range:focus::-moz-range-thumb {
                    box-shadow: 0 0 0 6px rgba(0,0,0,0.08);
                  }
                `}</style>

          </div>
        </div>
        <div className="mb-6">
          <ModalFieldLabel>Next Actions</ModalFieldLabel>
          <Textarea rows={3} value={form.next_actions} onChange={(e: any) => handleChange('next_actions', e.target.value)} />
        </div>


        <div className="flex items-center justify-end gap-2 mt-8">
          <StyledCancelButton onClick={onClose} />
          <StyledSaveButton onClick={handleSave} />
        </div>
      </div>

    </PositionedModal>
  );
};

export default CultureActionModal;
